import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  CreateMilestonePayload,
  MilestoneComment,
  MilestoneDetail,
  MilestoneListItem,
  PaginatedResponse,
  UpdateMilestonePayload,
} from "../types";

const MILESTONES_KEY = ["milestones"] as const;

export type MilestoneListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  priority?: string;
  category?: string;
  companyId?: string;
  productId?: string;
  projectId?: string;
  phaseId?: string;
  stageId?: string;
  meetingId?: string;
  ideaId?: string;
  ownerId?: string;
  overdue?: boolean;
  isFlagged?: boolean;
};

export function useMilestoneList(
  params: MilestoneListParams = {},
): UseQueryResult<PaginatedResponse<MilestoneListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...MILESTONES_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<MilestoneListItem>>("/milestones", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          priority: params.priority,
          category: params.category,
          companyId: params.companyId,
          productId: params.productId,
          projectId: params.projectId,
          phaseId: params.phaseId,
          stageId: params.stageId,
          meetingId: params.meetingId,
          ideaId: params.ideaId,
          ownerId: params.ownerId,
          overdue: params.overdue ? "true" : undefined,
          isFlagged:
            params.isFlagged === undefined
              ? undefined
              : params.isFlagged
                ? "true"
                : "false",
        },
      }),
  });
}

export function useMilestone(id: string): UseQueryResult<MilestoneDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [
      ...MILESTONES_KEY,
      "detail",
      id,
      accessToken ? "auth" : "anon",
    ],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ milestone: MilestoneDetail }>(
        `/milestones/${id}`,
        { token: accessToken },
      );
      return res.milestone;
    },
  });
}

export function useCreateMilestone() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMilestonePayload) =>
      apiFetch<{ milestone: MilestoneDetail }>("/milestones", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MILESTONES_KEY] });
      qc.invalidateQueries({ queryKey: ["meetings"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useUpdateMilestone(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateMilestonePayload) =>
      apiFetch<{ milestone: MilestoneDetail }>(`/milestones/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MILESTONES_KEY] });
    },
  });
}

export function useUpdateMilestoneStatus() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiFetch<{ milestone: MilestoneDetail }>(`/milestones/${id}/status`, {
        method: "PUT",
        body: { status },
        token: accessToken,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [...MILESTONES_KEY] });
      qc.invalidateQueries({
        queryKey: [...MILESTONES_KEY, "detail", vars.id],
      });
    },
  });
}

export function useUpdateMilestoneProgress() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, progress }: { id: string; progress: number }) =>
      apiFetch<{ milestone: MilestoneDetail }>(`/milestones/${id}/progress`, {
        method: "PUT",
        body: { progress },
        token: accessToken,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [...MILESTONES_KEY] });
      qc.invalidateQueries({
        queryKey: [...MILESTONES_KEY, "detail", vars.id],
      });
    },
  });
}

export function useToggleMilestoneFlag() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isFlagged }: { id: string; isFlagged: boolean }) =>
      apiFetch<{ milestone: MilestoneDetail }>(`/milestones/${id}/flag`, {
        method: "PUT",
        body: { isFlagged },
        token: accessToken,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [...MILESTONES_KEY] });
      qc.invalidateQueries({
        queryKey: [...MILESTONES_KEY, "detail", vars.id],
      });
    },
  });
}

export function useDeleteMilestone() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/milestones/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MILESTONES_KEY] });
    },
  });
}

export function useMilestoneComments(milestoneId: string) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [
      ...MILESTONES_KEY,
      milestoneId,
      "comments",
      accessToken ? "auth" : "anon",
    ],
    enabled: !!accessToken && !!milestoneId,
    queryFn: async () => {
      const res = await apiFetch<{ data: MilestoneComment[] }>(
        `/milestones/${milestoneId}/comments`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

export function useAddMilestoneComment(milestoneId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      apiFetch<{ comment: MilestoneComment }>(
        `/milestones/${milestoneId}/comments`,
        {
          method: "POST",
          body: { content },
          token: accessToken,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...MILESTONES_KEY, milestoneId, "comments"],
      });
    },
  });
}

export function useDeleteMilestoneComment(milestoneId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      apiFetch(`/milestones/${milestoneId}/comments/${commentId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...MILESTONES_KEY, milestoneId, "comments"],
      });
    },
  });
}
